const optionsComponent = require('../optionsComponent/optionsComponent');

const select = Object.assign({}, optionsComponent, {
    extraFields: {},
    forceEditInReadOnly: false, //read from data-zit-force-edit-in-read-only; when true a read-only select's filtered value is written back to the hidden input so it submits
    nullText: false, //read from data-zit-null-text; placeholder shown by a read-only select when no option matches the parent

    init: function () {
        optionsComponent.init.call(this);

        if (this.parentAttribute !== null) {
            this.element.addEventListener('change', () => { this.filterDependables(); });
        }
    },

    getOptionElements: function () {
        // Convert to Array: HTMLOptionsCollection has no forEach; NodeList does but options is not a NodeList
        if (typeof this.element.options !== typeof undefined) {
            return Array.from(this.element.options);
        } else {
            return [];
        }
    },

    getOptionId: function (optionElement) {
        return optionElement.value;
    },

    initOptions: function () {
        if (this.readOnly) {
            //read-only selects render a hidden input + text div, not <option> elements; the candidate
            //options come from data-zit-options (already parsed into this.options by readZAttributes).
            //normalise each so the read-only filtering can track per-option hidden state.
            let readOnlyOptions = Array.isArray(this.options) ? this.options : [];

            this.options = readOnlyOptions.map((readOnlyOption) => {
                return Object.assign({ _hiddenByFilters: {} }, readOnlyOption);
            });

            return;
        }

        let optionElements = this.getOptionElements();

        this.options = [];

        optionElements.forEach((optionElement) => {
            let optionExtraAttributes = this.hm.helper.parseJson(optionElement.dataset.zitExtraAttributes);

            let option = Object.assign({}, {
                _element: optionElement,
                _hiddenByFilters: {},
                id: this.getOptionId(optionElement)
            }, optionExtraAttributes);

            this.options.push(option);
        });
    },

    removeOption: function (option) {
        let foundIndex = this.findOptionIndex(option.id);

        if (foundIndex > -1) {
            if (foundIndex === this.element.selectedIndex) {
                this.setSelectedIndex(0);
            }

            this.element.remove(foundIndex);

            return true;
        }

        return false;
    },

    addOption: function (option) {
        let foundIndex = this.findOptionIndex(option.id);

        if (foundIndex === -1) {
            this.element.add(option._element);
            return true;
        }

        return false;
    },

    findOptionIndex: function (optionId) {
        let foundIndex = -1;
        let optionElements = this.getOptionElements();

        for (let i = 0; i < optionElements.length; i++) {
            let optionElement = optionElements[i];
            let currentOptionId = this.getOptionId(optionElement);

            if (this.equal(currentOptionId, optionId)) {
                foundIndex = i;
                break;
            }
        }

        return foundIndex;
    },

    setSelected: function (option) {
        let optionId = option;

        if (this.hm.helper.isObject(option) && typeof option.id !== typeof undefined) {
            optionId = option.id;
        }

        let foundIndex = this.findOptionIndex(optionId);

        this.setSelectedIndex(foundIndex);
    },

    setSelectedIndex: function (selectedIndex) {
        if (!this.readOnly) {
            if (this.element.options.length === 0) {
                selectedIndex = -1;
            }

            let changeHappened = false;

            if (selectedIndex >= 0) {
                if (this.selectedValue !== this.element.options[selectedIndex].value) {
                    this.selectedValue = this.element.options[selectedIndex].value;
                    changeHappened = true;
                }
            } else {
                this.selectedValue = '';
            }

            if (selectedIndex !== this.element.selectedIndex) {
                this.element.selectedIndex = selectedIndex;
                changeHappened = true;
            }

            if (changeHappened) {
                let ev = new Event('change');
                this.element.dispatchEvent(ev);
            }
        }
    },

    filterHide: function (attribute, value) {
        let fullComponentAction = optionsComponent.filterHide.call(this, attribute, value);

        if (typeof this.extraFields[attribute] === typeof undefined) {
            return true;
        }

        if (this.readOnly) {
            //read-only selects have no DOM <option> elements to add/remove; instead pick the
            //matching option and update the displayed text (and optionally the submit value).
            return this.filterHideReadOnly(attribute, value, fullComponentAction);
        }

        let somethingChanged = false;

        this.options.forEach((option) => {
            if (typeof option[attribute] === typeof undefined) {
                return true;
            }

            let optionValue = String(option[attribute]);

            let action = this.filterHideAction(optionValue, value);

            if (action === 'show') {
                //add the option to the select
                if (Object.hasOwn(option._hiddenByFilters, attribute)) {
                    delete option._hiddenByFilters[attribute];
                }
                if (Object.getOwnPropertyNames(option._hiddenByFilters).length === 0) {
                    let addOptionResult = this.addOption(option);
                    somethingChanged = somethingChanged || addOptionResult;
                }
            } else {
                //remove the option from the select
                option._hiddenByFilters[attribute] = value;
                let removeOptionResult = this.removeOption(option);
                somethingChanged = somethingChanged || removeOptionResult;
            }
        });

        this.filteredBy[attribute] = value;

        if (somethingChanged) {
            //in case the options of this select changed, trigger the change event to run the filterHide method again; this is helpful when there are more than 3 linked selects and select 1 changes the options in select 2, and after some options are changed in select 2, maybe select 3 needs to be filtered; running the method the second time produces a somethingChanged = false so there is no danger of entering an infinte loop
            let event = new Event('change');
            this.element.dispatchEvent(event);
        }

        return fullComponentAction;
    },

    filterHideReadOnly: function (attribute, value, fullComponentAction) {
        //no candidate options were loaded (retrieveOptionsWhenReadOnly not enabled): leave the
        //server-rendered text untouched rather than blanking it to the placeholder
        if (this.options.length === 0) {
            return fullComponentAction;
        }

        let somethingChanged = false;

        //filter the candidate options the same way the editable path does, but track visibility
        //in the in-memory options array since there are no DOM <option> elements to manipulate.
        this.options.forEach((option) => {
            if (typeof option[attribute] === typeof undefined) {
                return true;
            }

            let optionValue = String(option[attribute]);
            let action = this.filterHideAction(optionValue, value);

            if (action === 'show') {
                if (Object.hasOwn(option._hiddenByFilters, attribute)) {
                    delete option._hiddenByFilters[attribute];
                    somethingChanged = true;
                }
            } else {
                if (!Object.hasOwn(option._hiddenByFilters, attribute)) {
                    somethingChanged = true;
                }
                option._hiddenByFilters[attribute] = value;
            }
        });

        this.filteredBy[attribute] = value;

        //collect the options still visible after every active filter
        let visibleOptions = this.options.filter((option) => {
            return Object.getOwnPropertyNames(option._hiddenByFilters).length === 0;
        });

        //keep the current value if it is still visible, otherwise fall back to the first visible option
        let chosenOption = false;

        for (let i = 0; i < visibleOptions.length; i++) {
            if (this.equal(visibleOptions[i].id, this.selectedValue)) {
                chosenOption = visibleOptions[i];
                break;
            }
        }

        if (chosenOption === false && visibleOptions.length > 0) {
            chosenOption = visibleOptions[0];
        }

        this.applyReadOnlyDisplay(chosenOption);

        if (somethingChanged) {
            //cascade: this read-only select may itself be the parent of further dependants
            let event = new Event('change');
            this.element.dispatchEvent(event);
        }

        return fullComponentAction;
    },

    applyReadOnlyDisplay: function (option) {
        let textElement = document.getElementById(this.element.id + '_text');

        if (option === false) {
            //no option matches: show the null placeholder ('-' when no nullText is configured)
            let placeholder = (this.nullText !== false && this.nullText !== '') ? this.nullText : '-';

            if (textElement !== null) {
                textElement.textContent = placeholder;
            }

            //track the empty value so getValue (and any cascade to this select's own dependants)
            //stays consistent with the display
            this.selectedValue = '';

            //only write the submitted value when the field is allowed to persist on save
            if (this.forceEditInReadOnly) {
                this.element.value = this.selectedValue;
            }

            return;
        }

        if (textElement !== null) {
            textElement.textContent = option.name;
        }

        //track the displayed value so getValue (and any cascade to this select's own dependants)
        //reflects what is shown
        this.selectedValue = String(option.id);

        //when the field is allowed to write back on submit, also update the hidden input's value so
        //the derived value persists; otherwise the change stays purely presentational
        if (this.forceEditInReadOnly) {
            this.element.value = this.selectedValue;
        }
    },

    equal: function (left, right) {
        return String(left) === String(right);
    },

    getValue: function () {
        if (this.isHidden && this.hiddenValue !== false) {
            return this.hiddenValue;
        }

        if (this.element.nodeName.toLowerCase() == 'select') {
            if (this.element.selectedIndex >= 0) {
                this.selectedValue = this.element.options[this.element.selectedIndex].value;
            } else {
                this.selectedValue = '';
            }
        }

        return this.selectedValue;
    }

});

module.exports = select;